import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import type { Draft } from '@/types/api';
import { draftsLoadingAtom, updateDraftActionAtom } from '@/stores';

export interface DraftEditDialogProps {
  draft: Draft;
  assignmentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DraftEditDialog = ({
  draft,
  assignmentId,
  open,
  onOpenChange,
}: DraftEditDialogProps) => {
  const [value, setValue] = useState(draft.draftData ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const draftsLoading = useAtomValue(draftsLoadingAtom);
  const updateDraft = useSetAtom(updateDraftActionAtom);

  useEffect(() => {
    if (open) {
      setValue(draft.draftData ?? '');
    }
  }, [draft.draftData, open]);

  const originalValue = useMemo(() => draft.draftData ?? '', [draft.draftData]);
  const isDirty = value !== originalValue;
  const isDisabled = !isDirty || draftsLoading || isSaving;

  const handleSave = useCallback(async () => {
    if (!draft.id || !isDirty) {
      return;
    }

    setIsSaving(true);
    try {
      await updateDraft(assignmentId, draft.id, value);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  }, [assignmentId, draft.id, isDirty, onOpenChange, updateDraft, value]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isSaving) {
      onOpenChange(nextOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[650px]'>
        <DialogHeader>
          <DialogTitle>Edit Draft</DialogTitle>
          <DialogDescription>
            Update the draft content below and save your changes.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={value}
          onChange={event => setValue(event.target.value)}
          rows={12}
          className='text-sm'
        />
        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type='button' onClick={handleSave} disabled={isDisabled}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
